import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import {
  ArrowRight,
  Check,
  CheckCircle,
  Clock,
  Github,
  Globe,
  Play,
  Shield,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Slack,
  MessageSquare,
  Terminal,
  Cloud,
  Rocket,
  History,
} from "./Icons";
import { HeroAnimation } from "./HeroAnimation";
import { Footer } from "./Footer";
import { Header } from "./Header";

interface LandingProps {
  onEnterApp: () => void;
}

const useCases = [
  {
    icon: Globe,
    title: "Cloud Migration",
    description:
      "Orchestrate complex multi-step cloud migrations with clear dependencies and rollback plans.",
    color: "bg-blue-500",
  },
  {
    icon: Rocket,
    title: "Deployment",
    description:
      "Standardise your deployment runbooks with automated checks and manual approvals.",
    color: "bg-indigo-500",
  },
  {
    icon: Shield,
    title: "Incident Response",
    description:
      "Execute incident playbooks consistently, even under pressure. Track every action.",
    color: "bg-red-500",
  },
  {
    icon: Clock,
    title: "Scheduled Maintenance",
    description:
      "Plan maintenance windows with precision. Schedule tasks and get real-time visibility.",
    color: "bg-amber-500",
  },
  {
    icon: Users,
    title: "Team Coordination",
    description:
      "Assign tasks across teams and track who did what. Perfect for cross-functional cutover.",
    color: "bg-green-500",
  },
  {
    icon: CheckCircle,
    title: "Compliance Audits",
    description:
      "Maintain detailed audit trails of every execution for compliance and reporting.",
    color: "bg-purple-500",
  },
];

const features = [
  {
    title: "Visual Workflow Builder",
    description:
      "Design complex workflows with an intuitive drag-and-drop interface. Define dependencies, set durations, and assign tasks to specific team members.",
    image: "builder",
  },
  {
    title: "Real-Time Execution",
    description:
      "Track progress as it happens with live updates. See who's working on what, estimated completion times, and get notified when you're needed.",
    image: "runner",
  },
  {
    title: "Progressive Automation",
    description:
      "Start with manual checklists. Gradually replace manual steps with GitHub Actions workflows as you mature your processes. More integrations coming soon.",
    image: "github",
  },
  {
    title: "Smart Plan Suggestions",
    description:
      "Taskplan learns from every run. Get intelligent suggestions to adjust task durations based on actual execution times, keeping your estimates accurate.",
    image: "suggestions",
  },
  {
    title: "Audit and Compliance",
    description:
      "Every action, change, and completion is automatically recorded. Maintain a clear audit trail for compliance, post-mortems, and team accountability.",
    image: "activity",
  },
];

const testimonials = [
  {
    quote:
      "Taskplan transformed how we handle deployments. What used to be a stressful all-hands event is now a smooth, documented process.",
    author: "Sarah Chen",
    role: "Platform Lead",
    company: "TechStart Inc",
  },
  {
    quote:
      "The timeline view alone has saved us countless hours coordinating between teams during our cloud migration.",
    author: "Marcus Williams",
    role: "SRE Manager",
    company: "DataFlow Systems",
  },
  {
    quote:
      "Finally, a tool that bridges the gap between our runbook docs and actual execution. Our incident response time improved by 40%.",
    author: "Alex Rivera",
    role: "Operations Director",
    company: "FinanceHub",
  },
];

const rotatingPhrases = [
  { verb: "Automate", noun: "runbooks" },
  { verb: "Orchestrate", noun: "deployments" },
  { verb: "Simplify", noun: "migrations" },
  { verb: "Streamline", noun: "cutovers" },
  { verb: "Coordinate", noun: "incident response" },
];

export default function Landing({ onEnterApp }: LandingProps) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % rotatingPhrases.length);
        setIsAnimating(false);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-200">
      <Header onEnterApp={onEnterApp} />

      {/* Hero Section */}
      <main className="pt-32 pb-16 sm:pt-40 sm:pb-24 lg:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="lg:pt-6 sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left">
              <h1 className="text-4xl tracking-tight font-extrabold text-slate-900 dark:text-white text-5xl md:text-6xl lg:text-5xl xl:text-6xl">
                <span
                  className={`inline-block text-indigo-300 transition-all duration-300 ${isAnimating ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}
                >
                  {rotatingPhrases[phraseIndex].verb}
                </span>{" "}
                <span className="block xl:inline">your</span>{" "}
                <span
                  className={`block text-indigo-600 xl:inline transition-all duration-300 ${isAnimating ? "opacity-0 -translate-y-2" : "opacity-100 translate-y-0"}`}
                >
                  {rotatingPhrases[phraseIndex].noun}
                </span>
              </h1>
              <p className="mt-4 text-base text-slate-500 dark:text-slate-400 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                Orchestrate complex workflows with ease. Visual builder, team
                collaboration, and progressive automation for your engineering
                operations.
              </p>
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-4 md:justify-center lg:justify-start">
                  <SignedIn>
                    <button
                      onClick={onEnterApp}
                      className="flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg shadow-lg hover:shadow-xl transition-all"
                    >
                      Get started
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </button>
                  </SignedIn>
                  <SignedOut>
                    <SignInButton mode="modal">
                      <button className="flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg shadow-lg hover:shadow-xl transition-all">
                        Get started
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </button>
                    </SignInButton>
                  </SignedOut>
                </div>
                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                  No credit card required · Free for small teams
                </p>
              </div>
            </div>
            <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
              <HeroAnimation />
            </div>
          </div>
        </div>
      </main>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              Built for complex operations
            </h2>
            <p className="mt-4 text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              From cloud migrations to incident response, Taskplan helps teams
              execute critical workflows with confidence.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => {
              const Icon = useCase.icon;
              return (
                <div
                  key={useCase.title}
                  className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 hover:shadow-lg hover:border-slate-200 dark:hover:border-slate-700 transition-all group"
                >
                  <div
                    className={`h-12 w-12 flex items-center justify-center rounded-xl ${useCase.color} text-white mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="py-20 sm:py-28 bg-slate-50 dark:bg-slate-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              Everything you need to orchestrate
            </h2>
            <p className="mt-4 text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              Design, execute, and improve your workflows with tools built
              specifically for modern engineering teams. Start manual, automate
              as you grow.
            </p>
          </div>
          <div className="space-y-20">
            {/* Visual Workflow Builder */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 lg:max-w-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                  {features[0].title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {features[0].description}
                </p>
              </div>
              <div className="flex-1 w-full md:w-4/5 md:mx-auto lg:w-full lg:mx-0 bg-white dark:bg-slate-950 rounded-2xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800 relative group aspect-video">
                {/* Dot Grid Background */}
                <div className="absolute inset-0 bg-[linear-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,black,transparent)] dark:bg-[linear-gradient(#1f2937_1px,transparent_1px)] opacity-20"></div>

                {/* Sidebar Palette - Now Horizontal Bottom Left */}
                <div className="absolute left-4 bottom-4 flex flex-row gap-3 p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl z-20">
                  <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:scale-105 transition-transform cursor-grab">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-400 hover:scale-105 transition-transform cursor-grab">
                    <Users className="w-4 h-4" />
                  </div>
                  <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-400 hover:scale-105 transition-transform cursor-grab">
                    <Clock className="w-4 h-4" />
                  </div>
                </div>

                {/* Builder Canvas Area */}
                <div className="absolute inset-0">
                  <div className="relative w-full h-full">
                    {/* Connection Lines & Dots */}
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none z-0"
                      viewBox="0 0 100 56.25"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      {/* Connection Paths */}
                      <g
                        className="text-slate-300 dark:text-slate-700"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="0.4"
                        strokeDasharray="1,1"
                      >
                        <path d="M 26 28.125 C 31.5 28.125, 31.5 16, 37 16" />
                        <path d="M 26 28.125 C 31.5 28.125, 31.5 40.25, 37 40.25" />
                        <path d="M 63 16 C 68.5 16, 68.5 28.125, 74 28.125" />
                        <path d="M 63 40.25 C 68.5 40.25, 68.5 28.125, 74 28.125" />
                      </g>

                      {/* Connector Dots */}
                      <g
                        className="fill-slate-200 dark:fill-slate-700 stroke-white dark:stroke-slate-950"
                        strokeWidth="0.15"
                      >
                        <circle cx="26" cy="28.125" r="0.6" />
                        <circle cx="37" cy="16" r="0.6" />
                        <circle cx="37" cy="40.25" r="0.6" />
                        <circle cx="63" cy="16" r="0.6" />
                        <circle cx="63" cy="40.25" r="0.6" />
                        <circle cx="74" cy="28.125" r="0.6" />
                      </g>
                    </svg>

                    {/* Nodes */}
                    <div className="absolute top-[50%] left-[15%] -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-lg px-6 py-3 flex items-center gap-2 shadow-lg border border-slate-200 dark:border-slate-700 z-10 w-[22%] group/node">
                      <div className="p-1.5 rounded-md bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                        <Play className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200 truncate">
                          Start
                        </span>
                        <span className="text-[10px] text-slate-400 truncate uppercase tracking-tight">
                          Trigger
                        </span>
                      </div>
                    </div>

                    <div className="absolute top-[28.4%] left-[50%] -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-lg px-6 py-3 flex items-center gap-2 shadow-lg border border-indigo-200 dark:border-indigo-800 z-10 w-[26%] ring-2 ring-indigo-50 dark:ring-indigo-900/20">
                      <div className="p-1.5 rounded-md bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Zap className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200 truncate">
                          Deploy API
                        </span>
                        <span className="text-[10px] text-slate-400 truncate uppercase tracking-tight">
                          GitHub Action
                        </span>
                      </div>
                    </div>

                    <div className="absolute top-[71.6%] left-[50%] -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-lg px-6 py-3 flex items-center gap-2 shadow-lg border border-slate-200 dark:border-slate-700 z-10 w-[26%] opacity-60">
                      <div className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200 truncate">
                          Deploy UI
                        </span>
                        <span className="text-[10px] text-slate-400 truncate uppercase tracking-tight">
                          Manual Task
                        </span>
                      </div>
                    </div>

                    <div className="absolute top-[50%] left-[85%] -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-lg px-6 py-3 flex items-center gap-2 shadow-lg border border-slate-200 dark:border-slate-700 z-10 w-[22%]">
                      <div className="p-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <Shield className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200 truncate">
                          Verify
                        </span>
                        <span className="text-[10px] text-slate-400 truncate uppercase tracking-tight">
                          Approval
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
              <div className="flex-1 lg:max-w-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                  {features[1].title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {features[1].description}
                </p>
              </div>
              <div className="flex-1 w-full md:w-4/5 md:mx-auto lg:w-full lg:mx-0 bg-slate-900 rounded-2xl shadow-xl overflow-hidden border border-slate-800 relative group aspect-video flex flex-col">
                <div className="p-3 border-b border-slate-800 flex items-center gap-2 bg-slate-900/50 backdrop-blur">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/50"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/50"></div>
                  </div>
                  <div className="ml-4 px-2 py-0.5 rounded bg-slate-800 text-[10px] items-center text-slate-400 font-mono hidden sm:flex">
                    Production migration -{" "}
                    {new Date().toISOString().slice(0, 10)}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <span className="text-[10px] font-bold text-green-400 tracking-wider">
                      LIVE
                    </span>
                  </div>
                </div>
                <div className="flex-1 p-6 flex flex-col justify-center gap-4 relative">
                  <div className="absolute left-[37px] top-6 bottom-6 w-0.5 bg-slate-800 z-0"></div>

                  <div className="flex items-center gap-4 relative z-10 opacity-50">
                    <div className="w-7 h-7 rounded-full bg-slate-800 border-2 border-green-500/50 flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <div className="flex-1 bg-slate-800/50 rounded p-2 flex justify-between items-center border border-slate-800">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-slate-400 font-mono">
                          Backup database
                        </span>
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-700/30 border border-slate-600/50">
                            <span className="text-[9px] text-slate-500 font-medium whitespace-nowrap">
                              Joe Bloggs
                            </span>
                            <Check className="w-2.5 h-2.5 text-green-500/50" />
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-green-500/70">1m 24s</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 relative z-10">
                    <div className="w-7 h-7 rounded-full bg-indigo-500/20 border-2 border-indigo-500 flex items-center justify-center shadow-[0_0_15px_rgba(99,102,241,0.3)]">
                      <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                    </div>
                    <div className="flex-1 bg-slate-800 rounded p-2.5 flex justify-between items-center border border-indigo-500/30 shadow-lg">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-indigo-300 font-mono font-bold">
                          Run migrations
                        </span>
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center px-1.5 py-0.5 rounded-md bg-indigo-500/10 border border-indigo-500/20">
                            <span className="text-[9px] text-indigo-400 font-bold whitespace-nowrap">
                              DevOps Team
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-indigo-400 animate-pulse">
                        Running...
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 relative z-10 opacity-30">
                    <div className="w-7 h-7 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-700"></div>
                    </div>
                    <div className="flex-1 bg-slate-800/30 rounded p-2 border border-slate-800">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-500 font-mono">
                          Deploy services
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Integrations Ecosystem */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 lg:max-w-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                  {features[2].title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {features[2].description}
                </p>
              </div>
              <div className="flex-1 w-full md:w-4/5 md:mx-auto lg:w-full lg:mx-0 bg-slate-950 rounded-2xl shadow-2xl overflow-hidden border border-slate-800 relative group aspect-video flex items-center justify-center">
                <div className="absolute inset-0 bg-[#05070a]">
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage:
                        "linear-gradient(#1f2937 1px, transparent 1px), linear-gradient(90deg, #1f2937 1px, transparent 1px)",
                      backgroundSize: "30px 30px",
                    }}
                  ></div>
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 56.25"
                  >
                    <defs>
                      <filter id="glow">
                        <feGaussianBlur
                          stdDeviation="0.5"
                          result="coloredBlur"
                        />
                        <feMerge>
                          <feMergeNode in="coloredBlur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <g fill="none" strokeWidth="0.3" strokeOpacity="0.2">
                      <path
                        d="M 50 28.125 L 50 15 L 65 15"
                        stroke="#22c55e"
                        strokeOpacity="0.5"
                        filter="url(#glow)"
                      />
                      <path d="M 50 28.125 L 50 15 L 35 15" stroke="#64748b" />
                      <path
                        d="M 44 28.125 L 30 28.125 L 30 38"
                        stroke="#64748b"
                      />
                      <path
                        d="M 56 28.125 L 70 28.125 L 70 38"
                        stroke="#64748b"
                      />
                      <path d="M 50 34 L 50 42" stroke="#64748b" />
                    </g>
                    <g filter="url(#glow)">
                      <circle r="0.6" fill="#22c55e">
                        <animateMotion
                          path="M 50 28.125 L 50 15 L 65 15"
                          dur="3s"
                          repeatCount="indefinite"
                        />
                      </circle>
                      <circle r="0.4" fill="#fff">
                        <animateMotion
                          path="M 50 28.125 L 50 15 L 65 15"
                          dur="3s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    </g>
                  </svg>
                </div>
                <div className="absolute top-[26.6%] left-[65%] -translate-x-1/2 -translate-y-1/2 z-10 w-[10%]">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="w-full aspect-square rounded-lg bg-[#0D1117] border border-green-500/50 shadow-[0_0_15px_rgba(34,197,94,0.3)] flex items-center justify-center relative group-hover:scale-110 transition-transform">
                      <Github className="w-1/2 h-1/2 text-white" />
                      <div className="absolute -top-1 -right-1 w-1/4 h-1/4 rounded-full bg-green-500 border-2 border-slate-950 animate-pulse"></div>
                    </div>
                    <span className="text-[1.2vw] lg:text-[9px] font-mono text-green-400 uppercase tracking-tighter">
                      GitHub
                    </span>
                  </div>
                </div>
                <div className="absolute top-[26.6%] left-[35%] -translate-x-1/2 -translate-y-1/2 z-10 opacity-40 grayscale w-[8%]">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <Cloud className="w-1/2 h-1/2 text-orange-400" />
                    </div>
                    <span className="text-[1vw] lg:text-[8px] font-mono text-slate-500 uppercase tracking-tighter">
                      AWS
                    </span>
                  </div>
                </div>
                <div className="absolute top-[67.5%] left-[30%] -translate-x-1/2 -translate-y-1/2 z-10 opacity-40 grayscale w-[8%]">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <Slack className="w-1/2 h-1/2 text-white" />
                    </div>
                    <span className="text-[1vw] lg:text-[8px] font-mono text-slate-500 uppercase tracking-tighter">
                      Slack
                    </span>
                  </div>
                </div>
                <div className="absolute top-[67.5%] left-[70%] -translate-x-1/2 -translate-y-1/2 z-10 opacity-40 grayscale w-[8%]">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <MessageSquare className="w-1/2 h-1/2 text-blue-400" />
                    </div>
                    <span className="text-[1vw] lg:text-[8px] font-mono text-slate-500 uppercase tracking-tighter">
                      Teams
                    </span>
                  </div>
                </div>
                <div className="absolute top-[74.6%] left-[50%] -translate-x-1/2 -translate-y-1/2 z-10 opacity-40 grayscale w-[8%]">
                  <div className="flex flex-col items-center gap-1 w-full">
                    <div className="w-full aspect-square rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center">
                      <Terminal className="w-1/2 h-1/2 text-red-400" />
                    </div>
                    <span className="text-[1vw] lg:text-[8px] font-mono text-slate-500 uppercase tracking-tighter">
                      Jenkins
                    </span>
                  </div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-[14%]">
                  <div className="relative w-full">
                    <div className="w-full aspect-square rounded-xl bg-slate-900 border-2 border-indigo-500 shadow-[0_0_40px_rgba(99,102,241,0.4)] flex items-center justify-center relative overflow-hidden group-hover:border-indigo-400 transition-colors">
                      <div className="absolute inset-0 opacity-20 pointer-events-none">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-indigo-500"></div>
                        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-px bg-indigo-500"></div>
                      </div>
                      <Zap className="w-1/2 h-1/2 text-indigo-500 relative z-10" />
                    </div>
                    <div className="absolute -top-[10%] left-1/2 -translate-x-1/2 flex gap-[10%] w-1/2 justify-center">
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                    </div>
                    <div className="absolute -bottom-[10%] left-1/2 -translate-x-1/2 flex gap-[10%] w-1/2 justify-center">
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                      <div className="w-[10%] h-[12px] bg-indigo-500/50"></div>
                    </div>
                    <div className="absolute top-1/2 -left-[10%] -translate-y-1/2 flex flex-col gap-[10%] h-1/2 justify-center">
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                    </div>
                    <div className="absolute top-1/2 -right-[10%] -translate-y-1/2 flex flex-col gap-[10%] h-1/2 justify-center">
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                      <div className="h-[10%] w-[12px] bg-indigo-500/50"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Smart Suggestions */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
              <div className="flex-1 lg:max-w-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                  {features[3].title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {features[3].description}
                </p>
              </div>
              <div className="flex-1 w-full md:w-4/5 md:mx-auto lg:w-full lg:mx-0 bg-white dark:bg-slate-900 rounded-2xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800 relative group aspect-video flex flex-col p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                      Run Analysis
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      AI-powered optimization suggestions
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-amber-500"></div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          Deploy API
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400 line-through">
                            10m planned
                          </span>
                          <ArrowRight className="w-3 h-3 text-slate-300" />
                          <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                            15m actual
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center text-amber-500 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +50%
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <p className="text-xs text-slate-500 dark:text-slate-400 flex-1">
                        Consistently taking longer than planned.
                      </p>
                      <button className="text-xs font-medium bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 transition">
                        Update Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Log */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1 lg:max-w-lg">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                  {features[4].title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                  {features[4].description}
                </p>
              </div>
              <div className="flex-1 w-full md:w-4/5 md:mx-auto lg:w-full lg:mx-0 bg-white dark:bg-slate-950 rounded-2xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-800 relative group aspect-video p-6 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400">
                      <History className="w-5 h-5" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                      Activity Log
                    </h4>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                    Real-time
                  </span>
                </div>

                <div className="flex-1 space-y-4 overflow-hidden relative">
                  {/* Activity Items */}
                  {[
                    {
                      user: "Sarah Chen",
                      action: "completed",
                      target: "Backup database",
                      time: "2m ago",
                      color: "text-green-500",
                    },
                    {
                      user: "System",
                      action: "triggered",
                      target: "Run migrations",
                      time: "1m ago",
                      color: "text-indigo-500",
                    },
                    {
                      user: "Joe Bloggs",
                      action: "approved",
                      target: "Deploy API",
                      time: "Just now",
                      color: "text-amber-500",
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 relative">
                      <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-400 shrink-0">
                        {item.user[0]}
                      </div>
                      <div className="flex-1 min-w-0 border-b border-slate-50 dark:border-slate-800/50 pb-3">
                        <div className="flex justify-between items-start mb-0.5">
                          <p className="text-xs text-slate-900 dark:text-slate-100 font-medium">
                            <span className="font-bold">{item.user}</span>{" "}
                            {item.action}{" "}
                            <span className="text-indigo-600 dark:text-indigo-400">
                              {item.target}
                            </span>
                          </p>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {item.time}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${item.color.replace("text", "bg")}`}
                          ></div>
                          <span
                            className={`text-[10px] uppercase font-bold tracking-tight ${item.color}`}
                          >
                            {item.action}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Fading bottom */}
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white dark:from-slate-950 to-transparent pointer-events-none"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">
              Get started in minutes
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Build your workflow",
                description:
                  "Use the visual builder to create tasks, set dependencies, and assign team members.",
              },
              {
                step: "2",
                title: "Run it live",
                description:
                  "Execute your workflow with real-time tracking. Everyone sees progress as it happens.",
              },
              {
                step: "3",
                title: "Automate gradually",
                description:
                  "Connect GitHub Actions to automate repetitive tasks. Keep humans in the loop where it matters.",
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-2xl font-bold mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
            Ready to transform your operations?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join teams who have streamlined their workflows with Taskplan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 shadow-lg hover:shadow-xl transition-all">
                  Get started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <button
                onClick={onEnterApp}
                className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 shadow-lg hover:shadow-xl transition-all"
              >
                Get started
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </SignedIn>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
            >
              View pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
